import fetchBHApi from "@helper/fetchBHApi";
import { useCallback, useEffect, useState } from "react";

/**
 * Custom hook for making API requests using Fetch. This hook is used to fetch data from the API (POST/DELETE).
 * @param endpoint - The API endpoint to fetch data from.
 * @param method - Defaults to POST.
 * @param payload - Optional payload for POST or DELETE requests.
 * @param onLoad - If true, fetches data on mount using useEffect; otherwise, provides a manual fetch function.
 * @param T - Accepts the expected type from the API response.
 */
type FetchRequestProps = {
  endpoint: string;
  method?: "GET" | "POST" | "DELETE";
  payload?: (() => object) | object;
  skip?: boolean;
  onLoad?: boolean; // New flag to control automatic fetching
  headers?: (() => Record<string, string>) | Record<string, string>;
};

/**
 * Response structure for fetch requests.
 */
interface FetchResponse<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  fetchData?: () => Promise<void>; // Manual fetch function if onLoad is false
}

export function useFetchRequest<T>({
  endpoint,
  method = "GET",
  payload,
  skip = false,
  onLoad = true, // Default: Fetch on mount
  headers,
}: FetchRequestProps): FetchResponse<T> {
  const [state, setState] = useState<FetchResponse<T>>({
    data: null,
    error: null,
    isLoading: onLoad && !skip, // Only show loading if fetching on load
  });

  // Memoized function to prevent unnecessary re-renders
  const fetchData = useCallback(async () => {
    if (skip || !endpoint) return;

    try {
      setState({ data: null, error: null, isLoading: true });

      const payloadValue =
        typeof payload === "function" ? (payload() as object) : payload;
      const headersValue = typeof headers === "function" ? headers() : headers;

      const responseData = await fetchBHApi<T>(
        endpoint,
        method,
        payloadValue,
        headersValue,
      );
      setState({ data: responseData, error: null, isLoading: false });
    } catch (error) {
      setState({ data: null, error: error as Error, isLoading: false });
    }
  }, [skip, endpoint, method, payload, headers]);

  // Run fetchData only if onLoad is true
  useEffect(() => {
    if (onLoad) {
      void fetchData();
    }
  }, [fetchData, onLoad]);

  return onLoad ? state : { ...state, fetchData }; // Return fetchData function only if onLoad is false
}
